import React from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { FlowjoCommons } from '@portal/portal-core';
import { FjFlexContainer } from 'components/primitives';
import { useAppSelector } from 'store/hooks';

import { configurableStyling, alertTranslator, alertUiContainerIdentifier } from './alert-setup';
import { AlertContainer, Container, Header, HeaderContent, AlertX, ChildAlert } from './components';

const { Alerts } = FlowjoCommons;

// Minimal redux store shape used by the alert UI (only dispatch is accessed).
export interface AlertStore {
  dispatch: (action: unknown) => void;
}

interface AlertData {
  id: string;
  type: string | null;
  date?: string | null;
  filename?: string;
}

interface AlertUiProps {
  store: AlertStore;
}

const AlertUi = ({ store }: AlertUiProps): React.ReactElement | null => {
  const alerts = (useAppSelector((state) => state.alerts) || {}) as Record<string, AlertData>;

  const alertUiConfig = Alerts.AlertUiConfig.alertUiTypeConfig('cloud', configurableStyling);

  const alertMiddleArray: AlertData[] = [];
  const alertBottomArray: AlertData[] = [];
  Object.keys(alerts).forEach((alert) => {
    const alertObj = alerts[alert];
    const alertType = alertObj.type;
    const alertUiType = alertUiContainerIdentifier(alertType);
    if (alertUiType === 'alertBottom') {
      alertBottomArray.push(alertObj);
    } else if (alertUiType === 'alertMiddle') {
      alertMiddleArray.push(alertObj);
    }
  });
  const alertContainerArray: React.ReactElement[] = [];

  if (alertBottomArray) {
    alertContainerArray.push(
      <AlertsUiContainer
        store={store}
        key={alertContainerArray.length}
        alertUiContainerType="alertsBottom"
        alertUiConfig={alertUiConfig.alertBottom}
        alertsArray={alertBottomArray}
      />,
    );
  }

  if (alertMiddleArray) {
    alertContainerArray.push(
      <AlertsUiContainer
        store={store}
        key={alertContainerArray.length}
        alertUiContainerType="alertsMiddle"
        alertUiConfig={alertUiConfig.alertMiddle}
        alertsArray={alertMiddleArray}
      />,
    );
  }

  return alertContainerArray?.length > 0 ? <Container>{alertContainerArray}</Container> : null;
};

export default AlertUi;

interface AlertsUiContainerProps {
  store: AlertStore;
  alertUiContainerType: string;
  alertUiConfig: unknown;
  alertsArray: AlertData[];
}

interface AlertsUiContainerState {
  activeDrags: number;
  deltaPosition: {
    x: number;
    y: number;
  };
}

class AlertsUiContainer extends React.Component<AlertsUiContainerProps, AlertsUiContainerState> {
  draggableRef: React.RefObject<HTMLDivElement | null>;

  constructor(props: AlertsUiContainerProps) {
    super(props);
    this.state = {
      activeDrags: 0,
      deltaPosition: {
        x: 0,
        y: 0,
      },
    };
    this.draggableRef = React.createRef();
  }

  onStart(): void {
    this.setState({ activeDrags: this.state.activeDrags + 1 });
  }

  onStop(): void {
    this.setState({ activeDrags: this.state.activeDrags - 1 });
  }

  handleDrag(e: DraggableEvent, ui: DraggableData): void {
    e.preventDefault();
    const { x, y } = this.state.deltaPosition;
    this.setState({
      deltaPosition: {
        x: x + ui.deltaX,
        y: y + ui.deltaY,
      },
    });
  }

  dismissAllOfAlertsOfThisUiContainer(): void {
    const { store, alertsArray } = this.props;
    alertsArray.forEach((alert) => {
      if (alert.type) {
        store.dispatch(Alerts.AlertActions.dismissAlert(alert.id));
      }
    });
  }

  renderAlertsNow(): React.ReactElement | null {
    const { store, alertUiContainerType, alertsArray } = this.props;

    const renderAlertsArray: React.ReactElement[] = [];

    let headerContent: string | null | undefined;

    if (alertsArray?.length > 0) {
      if (alertsArray[0].type) {
        const translationFromErrorType = alertTranslator(alertsArray[0].type);
        headerContent = translationFromErrorType.title;
      } else if (alertsArray[0].filename) {
        headerContent = 'Uploads';
      }

      if (alertUiContainerType !== 'uploads') {
        for (const alertObj of alertsArray) {
          const translationFromErrorType = alertTranslator(alertObj.type);
          renderAlertsArray.push(
            <Alert
              store={store}
              key={alertObj.id}
              alertId={alertObj.id}
              content={translationFromErrorType.message}
              timeoutValue={translationFromErrorType.timeoutValue}
              showDismiss={translationFromErrorType.showDismissChild}
            />,
          );
        }
      }

      return (
        <div>
          <Draggable
            nodeRef={this.draggableRef}
            handle="div"
            bounds="body"
            onStart={() => {
              this.onStart();
            }}
            onDrag={(e: DraggableEvent, ui: DraggableData) => {
              this.handleDrag(e, ui);
            }}
            onStop={() => {
              this.onStop();
            }}
          >
            <AlertContainer
              ref={this.draggableRef}
              alertUiContainerType={
                alertUiContainerType as React.ComponentProps<
                  typeof AlertContainer
                >['alertUiContainerType']
              }
            >
              <Header>
                <HeaderContent>{headerContent}</HeaderContent>
                <AlertX
                  onClick={() => {
                    this.dismissAllOfAlertsOfThisUiContainer();
                  }}
                  onKeyPress={() => {
                    this.dismissAllOfAlertsOfThisUiContainer();
                  }}
                  role="link"
                  tabIndex={0}
                />
              </Header>
              {renderAlertsArray}
            </AlertContainer>
          </Draggable>
        </div>
      );
    }
    return null;
  }

  render(): React.ReactElement {
    return <div title="alerts-ui-container">{this.renderAlertsNow()}</div>;
  }
}

interface AlertProps {
  store: AlertStore;
  alertId: string;
  content: React.ReactNode;
  timeoutValue: number | null;
  showDismiss: boolean | null;
}

class Alert extends React.Component<AlertProps> {
  handleCloseClick(): void {
    this.removeSelf();
  }

  countdown(): void {
    setTimeout(() => {
      this.removeSelf();
    }, this.props.timeoutValue as number);
  }

  // Schedule auto-dismiss once on mount; calling countdown() in render() would
  // set a new timeout on every re-render.
  componentDidMount(): void {
    if (this.props.timeoutValue) {
      this.countdown();
    }
  }

  removeSelf(): void {
    const { store, alertId } = this.props;
    const action = Alerts.AlertActions.dismissAlert(alertId);
    store.dispatch(action);
  }

  render(): React.ReactElement {
    const { alertId, content, showDismiss } = this.props;

    return (
      <ChildAlert>
        <FjFlexContainer title={`alert ${alertId}`} role="textbox">
          {content}
        </FjFlexContainer>
        {showDismiss ? (
          <AlertX
            // `type` is spread onto the underlying span to preserve original DOM
            // output; AlertX's span props don't declare it, so cast at this boundary.
            {...({ type: 'error' } as React.ComponentProps<typeof AlertX>)}
            title={`dismiss ${alertId}`}
            id={alertId}
            onClick={() => {
              this.removeSelf();
            }}
            onKeyPress={() => {
              this.removeSelf();
            }}
            role="link"
            tabIndex={0}
          />
        ) : null}
      </ChildAlert>
    );
  }
}
